/**
 * RealtimeChat Component
 *
 * A real-time chat component with typing indicators, read receipts, and presence.
 * Works in both demo mode (mock data) and live mode (Supabase).
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  resolveReadReceipt,
  formatReadReceipt,
  type CounterpartyMark,
} from '@/lib/messaging/read-receipt';
import { useConversationReadMarks } from '@/lib/messaging/use-read-marks';
import Animated, { FadeIn, FadeInDown, SlideInUp } from 'react-native-reanimated';
import {
  Send,
  ChevronLeft,
  CheckCheck,
  Check,
  Circle,
  MessageCircle,
  MoreHorizontal,
} from 'lucide-react-native';
import { useQueryClient } from '@tanstack/react-query';
import { useThemeColors } from '@/lib/useThemeColors';
import { formatMessageTime } from '@/lib/format';
import { isWeb } from '@/lib/platform';
import {
  useRealtimeMessages,
  useTypingIndicator,
  usePresence,
  markMessagesAsRead,
  isSupabaseConfigured,
} from '@/lib/supabase';
import { mode } from '@/lib/config/app-mode';
import AnimatedPressable from '@/components/AnimatedPressable';
import { usePairBlock, type BlockSide, type ReportTarget } from '@/lib/moderation/api';
import { ConversationSafetyModal } from '@/components/moderation/ConversationSafetyModal';
import { ReportContentModal } from '@/components/moderation/ReportContentModal';
import { isInvestorErased } from '@/lib/supabase/messaging-mappers';
import { useCompanyPublicCard } from '@/lib/company/public-card';

/** Owner decision #9 (119-CONTEXT): a closed company's threads are kept, read-only. */
export const SEALED_THREAD_COPY = 'This company has closed. The conversation is read-only.';
/** A send refused by a read-only thread's RLS (42501) — e.g. sent before the seal was known. */
export const READ_ONLY_SEND_COPY = 'This conversation is read-only and no longer accepts messages.';

// ============================================
// Types
// ============================================

interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderType: 'startup' | 'investor';
  senderRole?: 'founder' | 'co-founder' | 'investor_relations' | 'other' | null;
  text: string;
  timestamp: string;
}

interface ChatConversation {
  id: string;
  startupId: string;
  startupName: string;
  /** NULL once the investor erased their account — see isInvestorErased(). */
  investorId: string | null;
  investorProfileId: string | null;
  investorName: string;
  /** Server-maintained per-side read marks. Whichever one is the COUNTERPARTY's
   *  decides Sent vs Read; see src/lib/messaging/read-receipt.ts. */
  startupLastReadAt?: string | null;
  investorLastReadAt?: string | null;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount: number;
  status: 'active' | 'archived';
}

interface RealtimeChatProps {
  conversation: ChatConversation;
  messages: ChatMessage[];
  currentUser: {
    id: string;
    name: string;
    type: 'startup' | 'investor';
    role?: string;
  };
  onBack: () => void;
  onSendMessage: (text: string) => void;
  isSending?: boolean;
  /**
   * An automated line shown at the end of the thread, DERIVED rather than
   * stored — currently the data-room rejection notice.
   *
   * Not a `messages` row on purpose: `sender_id`/`sender_type` are both NOT
   * NULL with no system-author concept, so persisting it would need either a
   * schema change or a sentinel uuid pretending to be a person. Deriving it
   * also means it cannot duplicate if a founder answers twice, cannot be
   * forged, and stays out of the founder's thread — the text is addressed to
   * the investor, and a stored row would show it to both.
   */
  systemNotice?: string | null;
}

// ============================================
// Typing Indicator Component
// ============================================

function TypingIndicator({ users }: { users: Array<{ name: string }> }) {
  const colors = useThemeColors();
  if (users.length === 0) return null;

  const names = users.map((u) => u.name.split(' ')[0]).join(', ');

  return (
    <Animated.View
      entering={FadeInDown.duration(200)}
      className="px-4 py-2 mb-2"
    >
      <View className="flex-row items-center gap-2">
        <View className="flex-row gap-1">
          {[0, 1, 2].map((i) => (
            <Animated.View
              key={i}
              className="w-2 h-2 rounded-full"
              style={{
                backgroundColor: colors.text.tertiary,
                opacity: 0.6,
              }}
            />
          ))}
        </View>
        <Text style={{ color: colors.text.tertiary }} className="text-xs">
          {names} {users.length === 1 ? 'is' : 'are'} typing...
        </Text>
      </View>
    </Animated.View>
  );
}

// ============================================
// Online Status Indicator
// ============================================

function OnlineStatus({ isOnline }: { isOnline: boolean }) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-center gap-1">
      <View
        className="w-2 h-2 rounded-full"
        style={{
          backgroundColor: isOnline ? colors.traffic.green : colors.text.tertiary,
        }}
      />
      <Text style={{ color: colors.text.tertiary }} className="text-xs">
        {isOnline ? 'Online' : 'Offline'}
      </Text>
    </View>
  );
}

// ============================================
// Message Bubble Component
// ============================================

/**
 * "Amara Diallo · Co-founder" — who actually wrote this.
 *
 * A conversation belongs to a (startup, investor) PAIR, so every active member
 * of the team writes into the same thread. Without a byline the investor sees
 * one anonymous voice, which is what this fixes.
 *
 * The role is dropped when it is 'other' (a non-founder team member): a literal
 * "Other" next to someone's name is worse than no label at all.
 */
function senderByline(message: ChatMessage): string {
  const role =
    message.senderRole === 'founder'
      ? 'Founder'
      : message.senderRole === 'co-founder'
        ? 'Co-founder'
        : message.senderRole === 'investor_relations'
          ? 'Investor Relations'
          : null;
  return role ? `${message.senderName} · ${role}` : message.senderName;
}

function MessageBubble({
  message,
  isOwnMessage,
  receipt,
  onLongPress,
}: {
  message: ChatMessage;
  isOwnMessage: boolean;
  /** Rendered only on the newest own message; null everywhere else. */
  receipt: { label: string; read: boolean } | null;
  /** Opens the report modal for this message. Only ever passed for the other party's messages — see the map below. */
  onLongPress?: () => void;
}) {
  const colors = useThemeColors();
  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      className={`mb-3 ${isOwnMessage ? 'items-end' : 'items-start'}`}
    >
      {/* Who wrote this. Received messages only — your own are already
          identified by side and colour, and in a shared team thread it is the
          OTHER people who need naming. */}
      {!isOwnMessage && (
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-xs mb-1 ml-1"
          numberOfLines={1}
        >
          {senderByline(message)}
        </Text>
      )}

      {/* Message bubble — long-press to report, other party's messages only */}
      <Pressable
        onLongPress={onLongPress}
        disabled={!onLongPress}
        accessibilityLabel={onLongPress ? 'Message. Long press to report' : undefined}
      >
        <View
          className="rounded-2xl px-4 py-3 max-w-[80%]"
          style={{
            backgroundColor: isOwnMessage ? colors.primary[600] : colors.surface.primary,
            borderBottomRightRadius: isOwnMessage ? 4 : 16,
            borderBottomLeftRadius: isOwnMessage ? 16 : 4,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: isOwnMessage ? 0.15 : 0.06,
            shadowRadius: 2,
            elevation: isOwnMessage ? 2 : 1,
          }}
        >
          <Text
            style={{ color: isOwnMessage ? colors.text.inverse : colors.text.primary }}
            className="text-sm"
          >
            {message.text}
          </Text>
        </View>
      </Pressable>

      {/* Timestamp and read receipt */}
      <View className="flex-row items-center gap-1 mt-1 mx-1">
        <Text style={{ color: colors.text.tertiary }} className="text-xs shrink" numberOfLines={1}>
          {formatMessageTime(message.timestamp)}
        </Text>
        {/* THE RECEIPT. On the newest own message only — iOS Messages does the
            same, and it is where the eye already is after sending. A tick under
            every run was four times the ink for the same one fact.

            Icon AND label: the icon is scannable at a glance, the label is what
            makes "Read by Maude" possible at all. Fades in on the flip to read,
            which is the only moment here worth animating. */}
        {receipt && (
          <Animated.View
            key={receipt.label}
            entering={FadeIn.duration(220)}
            className="flex-row items-center gap-1 shrink-0"
          >
            {receipt.read ? (
              <CheckCheck size={12} color={colors.primary[600]} />
            ) : (
              <Check size={12} color={colors.text.tertiary} />
            )}
            <Text
              style={{ color: receipt.read ? colors.primary[600] : colors.text.tertiary }}
              className="text-xs"
              numberOfLines={1}
            >
              {receipt.label}
            </Text>
          </Animated.View>
        )}
      </View>
    </Animated.View>
  );
}

// ============================================
// Main RealtimeChat Component
// ============================================

export function RealtimeChat({
  conversation,
  messages,
  currentUser,
  onBack,
  onSendMessage,
  systemNotice = null,
  isSending = false,
}: RealtimeChatProps) {
  const colors = useThemeColors();
  const scrollViewRef = useRef<ScrollView>(null);
  const [messageText, setMessageText] = useState('');
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: ReportTarget; id: string } | null>(null);

  // The investor side was erased (FK SET NULL keeps the founder's half). The
  // thread is read-only: RLS refuses new messages, and there is nobody left to
  // block or report, so the pair — and everything hanging off it — is null.
  const investorErased = isInvestorErased(conversation);

  // The company closed (119-07, owner decision #9). A returned public card IS
  // the "sealed" signal — company_public_card answers only for a sealed
  // company. The thread stays readable; every write to it is refused by RLS,
  // so the composer, the block action, typing and the read receipt are off.
  // Reporting stays: report-content writes no read-only table.
  const { card: sealedCard } = useCompanyPublicCard(conversation.startupId);
  const companySealed = sealedCard !== null;

  // Party-owned pair, not the two auth users — see src/lib/moderation/api.ts.
  // Null for a sealed company too: blocking would write conversation_blocks.
  const pair = conversation.investorProfileId === null || companySealed
    ? null
    : { startupId: conversation.startupId, investorProfileId: conversation.investorProfileId };
  const side = currentUser.type as BlockSide;
  const counterpartyName = currentUser.type === 'startup' ? conversation.investorName : conversation.startupName;
  const { block } = usePairBlock(pair, side);

  // Real-time subscriptions (only active when Supabase is configured)
  useRealtimeMessages(conversation.id);

  // THE RECEIPT.
  //
  // `counterpartyIsTeam` needs no server round trip: a conversation joins a
  // startup TEAM to exactly one investor profile, so an investor is looking at a
  // team (where a name is informative) and a founder is looking at one person
  // (where it only restates "Read").
  //
  // The side mark decides Sent vs Read and rides along with the conversation; the
  // names are a separate call, so the label degrades to a bare "Read" rather than
  // regressing to "Sent" while they load.
  const counterpartyIsTeam = currentUser.type === 'investor';
  const counterpartyLastReadAt = counterpartyIsTeam
    ? conversation.startupLastReadAt ?? null
    : conversation.investorLastReadAt ?? null;
  const counterpartyMarks: CounterpartyMark[] = useConversationReadMarks(conversation.id);

  // Only the NEWEST own message carries a receipt, so this is one lookup, not one
  // per bubble.
  const lastOwnMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].senderId === currentUser.id) return messages[i].id;
    }
    return null;
  }, [messages, currentUser.id]);

  // One stable object: both hooks list it as an effect dependency, so an inline
  // literal tore their Presence channels down and re-subscribed on every render.
  const presenceUser = useMemo(() => ({ id: currentUser.id, name: currentUser.name }), [currentUser.id, currentUser.name]);

  // Typing indicator (off for a read-only thread: nobody can type into it)
  const { typingUsers, setTyping } = useTypingIndicator(
    conversation.id,
    companySealed ? null : presenceUser
  );

  // Presence tracking
  const { isUserOnline } = usePresence(presenceUser, `conversation:${conversation.id}`);

  const queryClient = useQueryClient();

  // Get the other user's ID for presence check
  const otherUserId = currentUser.type === 'startup'
    ? conversation.investorId
    : conversation.startupId;
  const otherIsOnline = otherUserId !== null && isUserOnline(otherUserId);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages.length]);

  // Mark messages as read when viewing.
  //
  // AWAITED, and the cache invalidated afterwards. This was fire-and-forget:
  // the promise was dropped, so nothing ever ran once the reset landed and the
  // unread count went on being served from a stale cache — the conversation
  // list and (now) the tab badge kept showing a count for a thread you were
  // looking at. markMessagesAsRead reports its own write failures; what was
  // missing here was the refresh that makes a SUCCESSFUL reset visible.
  //
  // A sealed company's thread is read-only: the write would be refused, so it
  // is not attempted. If the seal is not known yet (the card still loading),
  // markMessagesAsRead answers 'read_only' for the refusal — quietly — and
  // nothing is invalidated, so the refused write cannot trigger itself again.
  useEffect(() => {
    if (companySealed) return;
    if (!(conversation.unreadCount > 0 && isSupabaseConfigured() && !mode.isMock)) return;

    let cancelled = false;
    void (async () => {
      const outcome = await markMessagesAsRead(conversation.id, currentUser.id);
      if (!cancelled && outcome === 'marked') {
        // Same key shape useRealtimeMessages invalidates — a prefix, so it
        // matches every per-persona conversations query.
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversation.id, conversation.unreadCount, currentUser.id, currentUser.type, queryClient, companySealed]);

  // Handle text input change with typing indicator
  const handleTextChange = useCallback(
    (text: string) => {
      setMessageText(text);
      if (text.length > 0) {
        setTyping(true);
      } else {
        setTyping(false);
      }
    },
    [setTyping]
  );

  // Handle send message
  const handleSend = useCallback(() => {
    if (!messageText.trim() || isSending) return;

    setTyping(false);
    onSendMessage(messageText.trim());
    setMessageText('');
  }, [messageText, isSending, setTyping, onSendMessage]);

  // Filter out current user from typing users
  const displayTypingUsers = typingUsers.filter((u) => u.id !== currentUser.id);

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Chat Header */}
          <View
            className="px-5 py-4 border-b"
            style={{
              backgroundColor: colors.surface.primary,
              borderColor: colors.border.light,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.04,
              shadowRadius: 3,
              elevation: 2,
            }}
          >
            <View className="flex-row items-center gap-3">
              <AnimatedPressable
                onPress={onBack}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </AnimatedPressable>

              {/* Avatar with online indicator */}
              <View className="relative">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primary[100] }}
                >
                  <Text style={{ color: colors.primary[600] }} className="font-bold">
                    {counterpartyName.charAt(0)}
                  </Text>
                </View>
                {/* Online indicator dot */}
                <View
                  className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2"
                  style={{
                    backgroundColor: otherIsOnline
                      ? colors.traffic.green
                      : colors.text.tertiary,
                    borderColor: colors.surface.primary,
                  }}
                />
              </View>

              <View className="flex-1">
                <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-semibold">
                  {counterpartyName}
                </Text>
                <OnlineStatus isOnline={otherIsOnline} />
              </View>

              {/* Conversation options — report / block / unblock (Guideline 1.2).
                  Absent once the investor is erased: there is no party left. */}
              {pair ? (
                <View className="shrink-0" style={{ width: 44, height: 44 }}>
                  <AnimatedPressable
                    onPress={() => setSafetyOpen(true)}
                    accessibilityLabel="Conversation options"
                    className="items-center justify-center"
                    style={{ flex: 1 }}
                  >
                    <MoreHorizontal size={22} color={colors.text.primary} />
                  </AnimatedPressable>
                </View>
              ) : null}
            </View>
          </View>

          {/* Messages */}
          <ScrollView
            ref={scrollViewRef}
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingVertical: 16 }}
          >
            {messages.length === 0 ? (
              <View className="items-center py-12">
                <MessageCircle size={48} color={colors.text.tertiary} />
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-center mt-4"
                >
                  No messages yet.{'\n'}Start the conversation!
                </Text>
              </View>
            ) : (
              messages.map((msg, index) => {
                // BY SENDER, not by side. Keyed on senderType, every message
                // from anyone on your own team rendered as YOURS — right
                // aligned, in the primary colour — so two founders sharing a
                // thread could not tell their messages apart, and neither
                // could long-press a teammate's message to report it.
                const isOwnMessage = msg.senderId === currentUser.id;
                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isOwnMessage={isOwnMessage}
                    receipt={
                      msg.id === lastOwnMessageId
                        ? (() => {
                            const r = resolveReadReceipt({
                              messageCreatedAt: msg.timestamp,
                              counterpartyLastReadAt,
                              counterpartyMarks,
                            });
                            return {
                              label: formatReadReceipt(r, { counterpartyIsTeam }),
                              read: r.state === 'read',
                            };
                          })()
                        : null
                    }
                    onLongPress={
                      isOwnMessage ? undefined : () => setReportTarget({ type: 'message', id: msg.id })
                    }
                  />
                );
              })
            )}

            {systemNotice ? (
              <View className="px-4 py-3 mt-2 mx-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text
                  className="text-xs text-center"
                  style={{ color: colors.text.secondary }}
                >
                  {systemNotice}
                </Text>
              </View>
            ) : null}

            {/* Typing indicator */}
            <TypingIndicator users={displayTypingUsers} />
          </ScrollView>

          {/* Message Input — replaced with a closed-composer notice when either
              side has blocked the other (App Store Guideline 1.2). */}
          {companySealed ? (
            <View
              testID="sealed-thread-notice"
              className="px-5 py-4 border-t items-center"
              style={{
                backgroundColor: colors.surface.primary,
                borderColor: colors.border.light,
              }}
            >
              <Text numberOfLines={2} style={{ color: colors.text.tertiary, textAlign: 'center' }}>
                {SEALED_THREAD_COPY}
              </Text>
            </View>
          ) : investorErased ? (
            <View
              className="px-5 py-4 border-t items-center"
              style={{
                backgroundColor: colors.surface.primary,
                borderColor: colors.border.light,
              }}
            >
              <Text numberOfLines={2} style={{ color: colors.text.tertiary, textAlign: 'center' }}>
                This investor deleted their account. The conversation is kept for your records.
              </Text>
            </View>
          ) : block ? (
            <View
              className="px-5 py-4 border-t items-center"
              style={{
                backgroundColor: colors.surface.primary,
                borderColor: colors.border.light,
              }}
            >
              <Text numberOfLines={2} style={{ color: colors.text.tertiary, textAlign: 'center' }}>
                {block.blockedByMySide
                  ? 'You blocked this conversation.'
                  : 'Messaging is no longer available in this conversation.'}
              </Text>
            </View>
          ) : (
            <View
              className="px-5 py-4 border-t"
              style={{
                backgroundColor: colors.surface.primary,
                borderColor: colors.border.light,
              }}
            >
              <View
                className="flex-row items-end gap-2 p-2 rounded-2xl"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <TextInput
                  className="flex-1 px-3 py-2"
                  style={{ color: colors.text.primary }}
                  placeholder="Type a message..."
                  placeholderTextColor={colors.text.tertiary}
                  value={messageText}
                  onChangeText={handleTextChange}
                  multiline
                  maxLength={500}
                  editable={!isSending}
                />
                <AnimatedPressable
                  onPress={handleSend}
                  disabled={!messageText.trim() || isSending}
                  className="w-10 h-10 rounded-xl items-center justify-center"
                  style={{
                    backgroundColor: messageText.trim() && !isSending
                      ? colors.primary[600]
                      : colors.light[300],
                  }}
                >
                  {isSending ? (
                    <ActivityIndicator size="small" color={colors.text.inverse} />
                  ) : (
                    <Send
                      size={18}
                      color={messageText.trim() ? colors.text.inverse : colors.text.tertiary}
                    />
                  )}
                </AnimatedPressable>
              </View>
            </View>
          )}
        </KeyboardAvoidingView>
      </SafeAreaView>

      {pair ? (
        <ConversationSafetyModal
          visible={safetyOpen}
          onClose={() => setSafetyOpen(false)}
          pair={pair}
          side={side}
          counterpartyName={counterpartyName}
          onReportConversation={() => setReportTarget({ type: 'conversation', id: conversation.id })}
        />
      ) : null}
      <ReportContentModal
        visible={!!reportTarget}
        onClose={() => setReportTarget(null)}
        target={reportTarget}
      />
    </View>
  );
}
